// Who is downstream of whom, and how full the buffer between them is.
//
// This is the part the priority rule stands on, and it used to be guessed. The
// guess was `mrp.workcenter.sequence`: take the next work centre on the line by
// that number. It is a display order over all sixty work centres and it does
// not follow any route — Pod Standing - Area C is 21, CNC (Biesse) is 22 — so
// it named a station's own supplier as its customer. Odoo's own work-order
// graph says the opposite and says it plainly: "Stand Pod" is *blocked by* CNC,
// Howick and Astron Tile QC, and *blocks* the eight flowline stations.
//
// So the route is read, never inferred, from three sources in this order:
//
//   1. `mrp.flowline.operation` — the published graph. Empty today; the process
//      editor fills it, and this picks it up the day it does.
//   2. `mrp.flowline.buffer` — each buffer names the operation that feeds it
//      and the one that draws from it, so a configured buffer is an edge as
//      well as a level. Empty today too.
//   3. The pods themselves — `blocked_by_workorder_ids` on the work orders of
//      the lead pod on each line. Populated right now, and per pod type, so an
//      Astron pod and a Tiled pod each get their own correct successor. The app
//      already draws a pod's route from these edges (`lib/upstreamGraph.ts`);
//      this reads the same edges a station at a time instead of a pod at a time.
//
// The fallback is per pair, not per app: a station whose route is published
// reads its successor from the graph while its neighbour still reads it from
// the pods. Nothing here ever falls back to work-centre sequence.

import type { BufferLineVM, FlowlineBufferVM, FlowlineOperationVM, RouteEdgeVM } from '@/api/types';

/** Which source named an edge. Shown nowhere; carried so tests and logs can say. */
export type RouteSource = 'operation' | 'buffer' | 'workorder';

/** One work order of one pod, reduced to what the route needs from it. */
export interface RouteStep {
  woId: number;
  moId: number;
  flowlineId: number;
  workcentreId: number;
  workcentreName: string;
  sequence: number;
  cancelled: boolean;
  /** Odoo's `blocked_by_workorder_ids` — the operations that must finish first. */
  blockedBy: number[];
}

/** A buffer with its endpoints resolved to work centres. */
export interface BufferEdge extends FlowlineBufferVM {
  fromWorkcentreId: number;
  toWorkcentreId: number;
}

export interface FlowlineTopology {
  operation: RouteEdgeVM[];
  buffer: RouteEdgeVM[];
  workorder: RouteEdgeVM[];
  buffers: BufferEdge[];
}

const key = (e: { flowlineId: number; fromWorkcentreId: number; toWorkcentreId: number }) =>
  `${e.flowlineId}:${e.fromWorkcentreId}:${e.toWorkcentreId}`;

/** Drop duplicates and any edge from a station to itself. */
function dedupe(edges: RouteEdgeVM[]): RouteEdgeVM[] {
  const seen = new Set<string>();
  const out: RouteEdgeVM[] = [];
  for (const e of edges) {
    if (!e.fromWorkcentreId || !e.toWorkcentreId || e.fromWorkcentreId === e.toWorkcentreId) continue;
    const k = key(e);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(e);
  }
  return out;
}

/**
 * Edges from the pods' own work orders.
 *
 * `blocked_by_workorder_ids` is the real graph and is used wherever the MO has
 * any: an edge runs from each blocking operation to the one it blocks. An MO
 * with no dependencies recorded at all falls back to its own sequence, one step
 * to the next — never to a sequence borrowed from anywhere else. Cancelled
 * operations are not part of anyone's route.
 */
export function edgesFromSteps(steps: RouteStep[]): RouteEdgeVM[] {
  const byMo = new Map<number, RouteStep[]>();
  for (const s of steps) {
    if (s.cancelled) continue;
    const list = byMo.get(s.moId);
    if (list) list.push(s);
    else byMo.set(s.moId, [s]);
  }

  const edges: RouteEdgeVM[] = [];
  for (const list of byMo.values()) {
    const byWo = new Map(list.map((s) => [s.woId, s]));
    const linked = list.some((s) => s.blockedBy.length > 0);
    if (linked) {
      for (const s of list) {
        for (const priorId of s.blockedBy) {
          const prior = byWo.get(priorId);
          if (!prior) continue;
          edges.push({
            flowlineId: s.flowlineId || prior.flowlineId,
            fromWorkcentreId: prior.workcentreId,
            toWorkcentreId: s.workcentreId,
            toWorkcentreName: s.workcentreName,
          });
        }
      }
      continue;
    }
    const ordered = [...list].sort((a, b) => a.sequence - b.sequence || a.woId - b.woId);
    for (let i = 0; i < ordered.length - 1; i += 1) {
      const from = ordered[i]!;
      const to = ordered[i + 1]!;
      edges.push({
        flowlineId: to.flowlineId || from.flowlineId,
        fromWorkcentreId: from.workcentreId,
        toWorkcentreId: to.workcentreId,
        toWorkcentreName: to.workcentreName,
      });
    }
  }
  return dedupe(edges);
}

/** Edges from the published graph: an operation feeds whatever it blocks. */
function edgesFromOperations(operations: FlowlineOperationVM[]): RouteEdgeVM[] {
  const byId = new Map(operations.map((o) => [o.id, o]));
  const edges: RouteEdgeVM[] = [];
  for (const op of operations) {
    const nextIds = new Set(op.blocks);
    if (op.coreSuccessorId) nextIds.add(op.coreSuccessorId);
    for (const nextId of nextIds) {
      const next = byId.get(nextId);
      if (!next) continue;
      edges.push({
        flowlineId: next.flowlineId || op.flowlineId,
        fromWorkcentreId: op.workcentreId,
        toWorkcentreId: next.workcentreId,
        toWorkcentreName: next.workcentreName,
      });
    }
    // `blocked_by` is the same graph read the other way. Reading both means a
    // half-published route — successors set on one side only — still connects.
    for (const priorId of op.blockedBy) {
      const prior = byId.get(priorId);
      if (!prior) continue;
      edges.push({
        flowlineId: op.flowlineId || prior.flowlineId,
        fromWorkcentreId: prior.workcentreId,
        toWorkcentreId: op.workcentreId,
        toWorkcentreName: op.workcentreName,
      });
    }
  }
  return dedupe(edges);
}

/**
 * A buffer-board row as the buffer the priority rule reads.
 *
 * The boards' `mrp.buffer.flowline` names its two work centres directly and
 * carries the same three levels the process editor's model would; only the
 * middle word differs — the board says `warning` where the editor says
 * `replenish`. Mapped here so the rule that decides "thin" has one vocabulary
 * whichever table the floor configured.
 */
export function bufferEdgeFromLine(line: BufferLineVM): BufferEdge | null {
  if (line.inWorkcentreId == null || line.outWorkcentreId == null || !line.flowlineId) return null;
  return {
    id: line.id,
    name: line.bufferName,
    flowlineId: line.flowlineId,
    fromOperationId: 0,
    toOperationId: 0,
    filled: line.filled,
    min: line.min,
    target: line.min,
    max: line.max,
    state: line.state === 'warning' ? 'replenish' : line.state,
    fromWorkcentreId: line.inWorkcentreId,
    toWorkcentreId: line.outWorkcentreId,
  };
}

/**
 * @param bufferLines The kiosk buffer boards' own table, which is the one with
 *   rows on it. Both buffer tables are read; a pair configured in either gets
 *   a level, and the process editor's wins where both name the same pair.
 */
export function buildTopology(
  operations: FlowlineOperationVM[],
  buffers: FlowlineBufferVM[],
  podEdges: RouteEdgeVM[],
  bufferLines: BufferLineVM[] = [],
): FlowlineTopology {
  const opById = new Map(operations.map((o) => [o.id, o]));
  const resolved: BufferEdge[] = [];
  const bufferEdges: RouteEdgeVM[] = [];
  for (const b of buffers) {
    const from = opById.get(b.fromOperationId);
    const to = opById.get(b.toOperationId);
    if (!from || !to) continue;
    resolved.push({ ...b, fromWorkcentreId: from.workcentreId, toWorkcentreId: to.workcentreId });
    bufferEdges.push({
      flowlineId: b.flowlineId || to.flowlineId,
      fromWorkcentreId: from.workcentreId,
      toWorkcentreId: to.workcentreId,
      toWorkcentreName: to.workcentreName,
    });
  }
  for (const line of bufferLines) {
    const edge = bufferEdgeFromLine(line);
    if (!edge) continue;
    const already = resolved.some(
      (r) =>
        r.flowlineId === edge.flowlineId &&
        r.fromWorkcentreId === edge.fromWorkcentreId &&
        r.toWorkcentreId === edge.toWorkcentreId,
    );
    if (!already) resolved.push(edge);
    bufferEdges.push({
      flowlineId: edge.flowlineId,
      fromWorkcentreId: edge.fromWorkcentreId,
      toWorkcentreId: edge.toWorkcentreId,
      toWorkcentreName: line.outWorkcentreName,
    });
  }
  return {
    operation: edgesFromOperations(operations),
    buffer: dedupe(bufferEdges),
    workorder: dedupe(podEdges),
    buffers: resolved,
  };
}

/** The stations this one feeds on this flowline, and which source said so. */
export function successorsOf(
  topology: FlowlineTopology,
  workcentreId: number,
  flowlineId: number,
): { workcentreId: number; workcentreName: string; source: RouteSource }[] {
  const tiers: Array<[RouteSource, RouteEdgeVM[]]> = [
    ['operation', topology.operation],
    ['buffer', topology.buffer],
    ['workorder', topology.workorder],
  ];
  for (const [source, edges] of tiers) {
    const hits = edges.filter((e) => e.fromWorkcentreId === workcentreId && e.flowlineId === flowlineId);
    if (hits.length === 0) continue;
    return hits.map((e) => ({
      workcentreId: e.toWorkcentreId,
      workcentreName: e.toWorkcentreName,
      source,
    }));
  }
  return [];
}

/**
 * The stations that feed this one on this flowline, and which source said so.
 *
 * The other direction of `successorsOf`, for the leader's starved rows: a
 * station with nothing ready is fixed at the station before it, so that is
 * where the row's button goes.
 */
export function predecessorsOf(
  topology: FlowlineTopology,
  workcentreId: number,
  flowlineId: number,
): { workcentreId: number; source: RouteSource }[] {
  const tiers: Array<[RouteSource, RouteEdgeVM[]]> = [
    ['operation', topology.operation],
    ['buffer', topology.buffer],
    ['workorder', topology.workorder],
  ];
  for (const [source, edges] of tiers) {
    const hits = edges.filter((e) => e.toWorkcentreId === workcentreId && e.flowlineId === flowlineId);
    if (hits.length === 0) continue;
    return hits.map((e) => ({ workcentreId: e.fromWorkcentreId, source }));
  }
  return [];
}

/** The buffer sitting between two stations, when one is configured. */
export function bufferBetween(
  topology: FlowlineTopology,
  flowlineId: number,
  fromWorkcentreId: number,
  toWorkcentreId: number,
): BufferEdge | null {
  return (
    topology.buffers.find(
      (b) =>
        b.flowlineId === flowlineId &&
        b.fromWorkcentreId === fromWorkcentreId &&
        b.toWorkcentreId === toWorkcentreId,
    ) ?? null
  );
}

/**
 * The stations of one flowline in route order, for a board that draws a column.
 *
 * A topological order over the edges, so a column reads the way the pods walk
 * it. Stations the route cannot place keep their given order at the end rather
 * than being dropped — a board that silently omits a station is worse than one
 * that shows it out of order.
 */
export function routeOrder(topology: FlowlineTopology, flowlineId: number, workcentreIds: number[]): number[] {
  const inScope = new Set(workcentreIds);
  const tiers = [topology.operation, topology.buffer, topology.workorder];
  const edges = (tiers.find((t) => t.some((e) => e.flowlineId === flowlineId)) ?? []).filter(
    (e) => e.flowlineId === flowlineId && inScope.has(e.fromWorkcentreId) && inScope.has(e.toWorkcentreId),
  );
  if (edges.length === 0) return [...workcentreIds];

  const indegree = new Map<number, number>(workcentreIds.map((w) => [w, 0]));
  const next = new Map<number, number[]>();
  for (const e of edges) {
    indegree.set(e.toWorkcentreId, (indegree.get(e.toWorkcentreId) ?? 0) + 1);
    next.set(e.fromWorkcentreId, [...(next.get(e.fromWorkcentreId) ?? []), e.toWorkcentreId]);
  }
  // Ready set kept in the caller's order, so equal candidates stay stable.
  const out: number[] = [];
  const placed = new Set<number>();
  let ready = workcentreIds.filter((w) => (indegree.get(w) ?? 0) === 0);
  while (ready.length) {
    const w = ready.shift()!;
    if (placed.has(w)) continue;
    placed.add(w);
    out.push(w);
    for (const to of next.get(w) ?? []) {
      const left = (indegree.get(to) ?? 0) - 1;
      indegree.set(to, left);
      if (left === 0) ready.push(to);
    }
    ready = ready.filter((r) => !placed.has(r));
  }
  // A cycle — a repair loop, say — leaves stations unplaced. They follow.
  for (const w of workcentreIds) if (!placed.has(w)) out.push(w);
  return out;
}
